import { Copy, MessageCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { track } from "../../lib/api";
import { buildOfferLink, resolveOfferRules, todayPlusIso } from "../../lib/offers";
import { useSettings } from "../../context/SettingsContext";
import { fmtPKR } from "./Blocks";

const engineDateLabel = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const OfferCard = ({ airline, dest, page }) => {
  const { settings } = useSettings();
  const link = buildOfferLink(settings, airline, dest);
  const rules = resolveOfferRules(settings, airline, dest);
  const departIso = todayPlusIso(Number(rules.days_ahead) || 2);

  const book = () => {
    track("booking_handoff", page, { source: "offer_card", dest: dest.code, url: link });
    window.open(link, "_blank", "noopener");
  };

  const shortLink = `${window.location.origin}/go/${airline.slug}/${dest.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shortLink);
      toast.success("Campaign link copied — stable URL, always fresh dates");
    } catch {
      toast.error("Could not copy link");
    }
    track("offer_link_copied", page, { dest: dest.code, link: shortLink });
  };

  const waShare = `https://wa.me/?text=${encodeURIComponent(
    `${airline.name} deal: ${(rules.dep || airline.default_dep)} → ${dest.city} (${dest.code}) from ${fmtPKR(dest.price_from)}, departing ${engineDateLabel(departIso)}. Book live fares here: ${shortLink}`
  )}`;

  return (
    <div className="border-b border-slate-100 pb-3 last:border-0" data-testid={`offer-card-${dest.code}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm text-slate-800">{dest.city}</p>
          <p className="text-xs text-slate-400">
            {dest.country} · {dest.code} · dep {engineDateLabel(departIso)}
          </p>
        </div>
        <span className="text-sm font-bold text-[#B01115]">{fmtPKR(dest.price_from)}</span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button
          data-testid={`offer-book-${dest.code}`}
          onClick={book}
          className="flex items-center gap-1 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors duration-200"
        >
          <ExternalLink className="w-3 h-3" /> Book
        </button>
        <button
          data-testid={`offer-copy-${dest.code}`}
          onClick={copy}
          title="Copy shareable link for ads & marketing"
          className="flex items-center gap-1 border border-slate-200 hover:border-[#970C10] text-slate-600 hover:text-[#970C10] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200"
        >
          <Copy className="w-3 h-3" /> Copy link
        </button>
        <a
          data-testid={`offer-wa-${dest.code}`}
          href={waShare}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("offer_wa_shared", page, { dest: dest.code })}
          title="Share on WhatsApp"
          className="flex items-center gap-1 border border-slate-200 hover:border-[#25D366] text-slate-600 hover:text-[#25D366] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200"
        >
          <MessageCircle className="w-3 h-3" /> Share
        </a>
      </div>
    </div>
  );
};
